import { Component, input, output, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { ToastComponent } from '../shared/toast.component';
import { UsersService } from './users.service';
import { SpinnerComponent } from '../shared/spinner.component';

@Component({
  selector: 'eaa-reset-user-password-dialog',
  imports: [DialogModule, ReactiveFormsModule, SpinnerComponent, ToastComponent],
  template: `
    <ejs-dialog
      #dgResetPassword
      [showCloseIcon]="!processing"
      [isModal]="true"
      [visible]="false"
      width="800px"
      [closeOnEscape]="!processing"
    >
      <ng-template #content>
        @if (!newPassword) {
          <div class="alert alert-warning" role="alert">
            Please verify the legitimacy of the request with the client before proceeding with the password reset.
          </div>
          <p>Are you sure you want to reset the password of {{ userDisplayName() }} ({{ userEmail() }})?</p>
        }

        <!-- Show new password after successful reset -->
        @if (newPassword) {
          <div class="alert alert-success mt-3" role="alert">
            <strong>New Password Generated:</strong>
            <div class="d-flex align-items-center gap-2 mt-2">
              <code class="bg-light p-2 rounded">{{ newPassword }}</code>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                (click)="copyToClipboard()"
                title="Copy to clipboard"
              >
                <i class="bi bi-copy" aria-hidden="true"></i>
              </button>
            </div>
            <small class="text-muted mt-1 d-block">
              Please securely share this password with the user and advise them to change it on the first login.
            </small>
          </div>
        }
      </ng-template>
      <ng-template #header>{{
        newPassword ? 'Password Changed' : 'Reset password of ' + userDisplayName()
      }}</ng-template>
      <ng-template #footerTemplate>
        <div class="btn-group">
          <button class="btn btn-outline-secondary" (click)="close()" [disabled]="processing">
            {{ newPassword ? 'CLOSE' : 'CANCEL' }}
          </button>
          @if (!newPassword) {
            <button class="btn btn-primary" (click)="resetUserPassword()" [disabled]="processing">
              RESET PASSWORD
            </button>
          }
        </div>
      </ng-template>
    </ejs-dialog>

    <eaa-toast #toastResetPasswordAction [showCloseButton]="false">
      <div id="title">PROCESSING</div>
      <div id="content" class="d-flex gap-2">
        <eaa-spinner />
        <div>
          {{ processingMessage }}
        </div>
      </div>
    </eaa-toast>

    <!-- Toast for copy feedback -->
    <eaa-toast #toastCopyFeedback [showCloseButton]="false">
      <div id="title">SUCCESS</div>
      <div id="content">Password copied to clipboard!</div>
    </eaa-toast>
  `
})
export class ResetUserPasswordDialogComponent {
  public userDisplayName = input<string | undefined>('');
  public userEmail = input<string | undefined>('');
  public userId = input<string | undefined>('');
  public processingMessage = '';
  public resetComplete = output<void>();
  public processing = false;
  public newPassword = '';

  @ViewChild('dgResetPassword')
  public dialog: DialogComponent | null = null;

  @ViewChild('toastResetPasswordAction')
  public toastResetPasswordAction?: ToastComponent;

  @ViewChild('toastCopyFeedback')
  public toastCopyFeedback?: ToastComponent;

  constructor(private usersService: UsersService) {}

  public show(): void {
    // Reset state when showing dialog
    this.newPassword = '';
    this.processing = false;
    this.processingMessage = '';
    this.dialog?.show();
  }

  public close(): void {
    this.dialog?.hide();
  }

  public resetUserPassword() {
    if (this.userId() === undefined) {
      return;
    }

    this.processing = true;
    this.processingMessage = 'Resetting user password...';
    this.toastResetPasswordAction?.show();

    this.usersService.resetUserPassword$(this.userId()!).subscribe({
      next: (password: string) => this.handleAfterExecute(true, password),
      error: () => this.handleAfterExecute(false)
    });
  }

  public copyToClipboard(): void {
    if (this.newPassword) {
      navigator.clipboard
        .writeText(this.newPassword)
        .then(() => {
          this.toastCopyFeedback?.show();
          setTimeout(() => {
            this.toastCopyFeedback?.hide();
          }, 2000);
        })
        .catch((err) => {
          console.error('Failed to copy password to clipboard:', err);
        });
    }
  }

  private handleAfterExecute(success: boolean, password?: string): void {
    if (success && password) {
      this.newPassword = password;
      this.processingMessage = `Password reset successfully.`;
      this.resetComplete.emit();

      setTimeout(() => {
        this.resetToast();
      }, 1500);
    } else {
      this.resetToast();
    }

    this.processing = false;
  }

  private resetToast() {
    this.toastResetPasswordAction?.hide();
    this.processingMessage = '';
  }
}
